/**
 * The changes Auto show makes, one element at a time. Each takes the current scene and returns it
 * with one thing changed (or null when there's nothing it may change), choosing visualizations,
 * colours and effects that suit how intense the music is, avoiding recent ones, and leaving pinned
 * things alone.
 */
import type { EnergyTag } from '../shared/mood';
import { MAX_LAYERS, uid, type Layer, type Scene } from '../shared/scene';
import type { VisualizationDef } from '../shared/viz';
import { keepPinned, randomParams, surpriseScene, type Pins } from './variety';

export interface MoveContext {
  backgrounds: readonly VisualizationDef[];
  elements: readonly VisualizationDef[];
  palettes: readonly { id: string; stops: readonly string[] }[];
  pins: Pins;
  energy: EnergyTag;
  /** Recently used visualization and palette ids, newest first. */
  recent: readonly string[];
  rand: () => number;
}

/** At most this many layers when Auto show adds them (a background and two on top). */
const SHOW_MAX_LAYERS = 3;
/** The effects Auto show manages; other overall effects (bloom, colour…) are left as they are. */
const SHOW_FX = ['feedback', 'mirror'] as const;

const pick = <T>(xs: readonly T[], rand: () => number): T | undefined =>
  xs[Math.floor(rand() * xs.length)];

/** The ones tagged with this energy, if any are; otherwise all of them. */
export function forEnergy<T extends { tags?: readonly string[] }>(
  pool: readonly T[],
  energy: EnergyTag,
): readonly T[] {
  const matching = pool.filter((v) => v.tags?.includes(`energy:${energy}`));
  return matching.length ? matching : pool;
}

/** A pick suited to the energy, avoiding `avoid` (current and recent) where possible. */
export function pickFor<T extends { id: string; tags?: readonly string[] }>(
  pool: readonly T[],
  energy: EnergyTag,
  avoid: readonly string[],
  rand: () => number,
): T | undefined {
  const fresh = (xs: readonly T[]) => xs.filter((v) => !avoid.includes(v.id));
  const suited = forEnergy(pool, energy);
  return pick(fresh(suited), rand) ?? pick(fresh(pool), rand) ?? pick(suited, rand);
}

/** 0..1: how saturated and bright a palette is on average (calm music gets the softer ones). */
export function vividness(stops: readonly string[]): number {
  let sum = 0;
  for (const hex of stops) {
    const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    sum += (max === 0 ? 0 : (max - min) / max) * max;
  }
  return stops.length ? sum / stops.length : 0;
}

/** Palettes for the energy: the softest third for low, the most vivid third for high. */
export function palettesFor<T extends { stops: readonly string[] }>(
  palettes: readonly T[],
  energy: EnergyTag,
): T[] {
  const sorted = [...palettes].sort((a, b) => vividness(a.stops) - vividness(b.stops));
  const third = Math.max(1, Math.round(sorted.length / 3));
  if (energy === 'low') return sorted.slice(0, third);
  if (energy === 'high') return sorted.slice(-third);
  return sorted.slice(third, sorted.length - third);
}

/** Modulations without those that drive this layer. */
const dropModsOf = (scene: Scene, layerId: string) =>
  scene.modulations.filter((m) => !m.target.startsWith(`layer/${layerId}/`));

function freshLayer(def: VisualizationDef, rand: () => number, onTop: boolean): Layer {
  return {
    id: uid('l'),
    vizId: def.id,
    params: randomParams(def.params, rand),
    blend: onTop ? (def.blend ?? 'over') : 'normal',
    opacity: onTop ? 0.9 : 1,
    enabled: true,
  };
}

export function changeColours(scene: Scene, c: MoveContext): Scene | null {
  if (c.pins.palette) return null;
  const suited = palettesFor(c.palettes, c.energy);
  const p = pickFor(
    suited.length ? suited : c.palettes,
    c.energy,
    [scene.palette.id, ...c.recent],
    c.rand,
  );
  if (!p || p.id === scene.palette.id) return null;
  return {
    ...scene,
    palette: { id: p.id },
    paletteShift: 0,
    modulations: scene.modulations.filter((m) => m.target !== 'palette/shift'),
  };
}

export function changeBackground(scene: Scene, c: MoveContext): Scene | null {
  const bg = scene.layers[0];
  if (!bg || c.pins.layers.includes(bg.id)) return null;
  const def = pickFor(c.backgrounds, c.energy, [bg.vizId, ...c.recent], c.rand);
  if (!def || def.id === bg.vizId) return null;
  const layer = { ...freshLayer(def, c.rand, false), id: bg.id };
  return {
    ...scene,
    layers: [layer, ...scene.layers.slice(1)],
    modulations: dropModsOf(scene, bg.id),
  };
}

/** Swap the top layer for another, or add one (busier music), or take one away (calmer). */
export function changeTop(scene: Scene, c: MoveContext): Scene | null {
  const layers = scene.layers;
  const unpinned = layers
    .map((l, i) => ({ l, i }))
    .filter(({ l, i }) => i > 0 && !c.pins.layers.includes(l.id));
  const canAdd = layers.length < Math.min(SHOW_MAX_LAYERS, MAX_LAYERS);
  const onTop = layers.slice(1).map((l) => l.vizId);
  const add = (): Scene | null => {
    const def = pickFor(c.elements, c.energy, [...onTop, ...c.recent], c.rand);
    return def ? { ...scene, layers: [...layers, freshLayer(def, c.rand, true)] } : null;
  };

  if (layers.length === 1 || (unpinned.length === 0 && canAdd)) return add();
  if (c.energy === 'high' && canAdd && c.rand() < 0.35) return add();
  const top = unpinned.at(-1);
  if (!top) return null;
  if (c.energy === 'low' && layers.length >= 3) {
    return {
      ...scene,
      layers: layers.filter((l) => l.id !== top.l.id),
      modulations: dropModsOf(scene, top.l.id),
    };
  }
  const def = pickFor(c.elements, c.energy, [...onTop, ...c.recent], c.rand);
  if (!def || def.id === top.l.vizId) return null;
  const swapped = { ...freshLayer(def, c.rand, true), id: top.l.id };
  return {
    ...scene,
    layers: layers.map((l) => (l.id === top.l.id ? swapped : l)),
    modulations: dropModsOf(scene, top.l.id),
  };
}

/** New trails, mirrors or kaleidoscopes for the energy (or none), keeping other effects. */
export function showEffects(
  energy: EnergyTag,
  rand: () => number,
  allowNone = true,
): Scene['postfx'] {
  const [none, trails, mirror] =
    energy === 'low' ? [0.4, 0.8, 1] : energy === 'mid' ? [0.25, 0.6, 0.85] : [0.15, 0.5, 0.65];
  // Without the option of none, pick among the others in the same proportions.
  const r = allowNone ? rand() : none + rand() * (1 - none);
  if (r < none) return {};
  if (r < trails) {
    const amp = energy === 'high' ? 1 : 0.4;
    return {
      feedback: {
        enabled: 1,
        amount: 0.45 + rand() * 0.35,
        zoom: 1 + (rand() * 2 - 1) * 0.03 * amp,
        rotate: (rand() * 2 - 1) * 0.02 * amp,
      },
    };
  }
  if (r < mirror) return { mirror: { mode: 1 + Math.floor(rand() * 3) } };
  return {
    mirror: {
      mode: 4,
      segments: 4 + Math.floor(rand() * (energy === 'high' ? 9 : 5)),
      spin: (rand() * 2 - 1) * (energy === 'high' ? 0.3 : 0.12),
    },
  };
}

const showFxOf = (postfx: Scene['postfx']) => JSON.stringify(SHOW_FX.map((k) => postfx[k] ?? null));

export function changeEffects(scene: Scene, c: MoveContext): Scene | null {
  if (c.pins.effects) return null;
  const kept = Object.fromEntries(
    Object.entries(scene.postfx).filter(([k]) => !(SHOW_FX as readonly string[]).includes(k)),
  );
  // With no effects on, "none" wouldn't change anything: always bring one in.
  const hasAny = showFxOf(scene.postfx) !== showFxOf({});
  for (let tries = 0; tries < 8; tries++) {
    const postfx = { ...kept, ...showEffects(c.energy, c.rand, hasAny) };
    if (showFxOf(postfx) !== showFxOf(scene.postfx)) {
      return {
        ...scene,
        postfx,
        modulations: scene.modulations.filter(
          (m) => !SHOW_FX.some((fx) => m.target.startsWith(`fx/${fx}/`)),
        ),
      };
    }
  }
  return null;
}

/** A breakdown: strip back to the background (and anything pinned), and clear the effects. */
export function calmDown(scene: Scene, c: MoveContext): Scene | null {
  const keep = scene.layers.filter((l, i) => i === 0 || c.pins.layers.includes(l.id));
  const gone = scene.layers.filter((l) => !keep.includes(l)).map((l) => l.id);
  const postfx = c.pins.effects
    ? scene.postfx
    : Object.fromEntries(
        Object.entries(scene.postfx).filter(([k]) => !(SHOW_FX as readonly string[]).includes(k)),
      );
  if (gone.length === 0 && showFxOf(postfx) === showFxOf(scene.postfx)) return null;
  return {
    ...scene,
    layers: keep,
    postfx,
    modulations: scene.modulations.filter(
      (m) =>
        !gone.some((id) => m.target.startsWith(`layer/${id}/`)) &&
        (c.pins.effects || !SHOW_FX.some((fx) => m.target.startsWith(`fx/${fx}/`))),
    ),
  };
}

/** A whole new look for the energy, made up on the spot (pinned things kept). */
export function wholeNewLook(scene: Scene, c: MoveContext): Scene {
  const suited = forEnergy(c.backgrounds, c.energy);
  const fresh = suited.filter((v) => !c.recent.includes(v.id));
  const made = surpriseScene(
    scene,
    {
      backgrounds: fresh.length ? fresh : suited,
      elements: forEnergy(c.elements, c.energy),
      palettes: palettesFor(c.palettes, c.energy),
      elementChance: c.energy === 'low' ? 0.4 : c.energy === 'high' ? 0.9 : 0.65,
    },
    c.rand,
  );
  return keepPinned(scene, made, c.pins);
}
